// src/utils/billingApi.js
// Typed-ish wrapper around the /api/billing/* endpoints. Reuses the
// existing apiCall (Auth + retry + x-org-slug) so callers don't have to
// manage auth headers.

import { apiCall } from './apiUtils';

// ── Plans ────────────────────────────────────────────────────────────────────
// Plans are seeded per-currency. Callers pass `currency` to pick the row
// they want; backend defaults to BILLING.DEFAULT_CURRENCY when omitted.
export const listPlans = ({ currency } = {}, toast) => {
  const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
  return apiCall(`/api/billing/plans${qs}`, { method: 'GET' }, toast);
};

export const getPlan = (code, { currency } = {}, toast) => {
  const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
  return apiCall(`/api/billing/plans/${encodeURIComponent(code)}${qs}`, { method: 'GET' }, toast);
};

export const getGeoCurrency = (toast) =>
  apiCall('/api/billing/geo-currency', { method: 'GET' }, toast);

// ── Subscription ────────────────────────────────────────────────────────────
export const getMySubscription = (toast) =>
  apiCall('/api/billing/subscription', { method: 'GET' }, toast);

export const quoteSubscription = ({ planCode, billingCycle = 'monthly', currency }, toast) =>
  apiCall(
    '/api/billing/subscription/quote',
    { method: 'POST', body: JSON.stringify({ planCode, billingCycle, currency }) },
    toast
  );

export const initiatePurchase = ({ planCode, billingCycle = 'monthly', currency }, toast) =>
  apiCall(
    '/api/billing/subscription/initiate',
    { method: 'POST', body: JSON.stringify({ planCode, billingCycle, currency }) },
    toast
  );

export const confirmPayment = ({ orderId, paymentId, signature, invoiceId }, toast) =>
  apiCall(
    '/api/billing/subscription/confirm',
    {
      method: 'POST',
      body: JSON.stringify({ orderId, paymentId, signature, invoiceId }),
    },
    toast
  );

// Records a client-side payment failure (Razorpay's payment.failed handler
// fires before any backend webhook arrives in test mode).
export const recordPaymentFailure = ({ invoiceId, orderId, paymentId, code, description }) =>
  apiCall(
    '/api/billing/subscription/record-failure',
    {
      method: 'POST',
      body: JSON.stringify({ invoiceId, orderId, paymentId, code, description }),
    },
    // intentionally no toast — caller renders its own UI for failure
  );

export const cancelSubscription = ({ reason } = {}, toast) =>
  apiCall(
    '/api/billing/subscription/cancel',
    { method: 'POST', body: JSON.stringify({ reason: reason || null }) },
    toast
  );

export const requestEnterpriseUpgrade = (
  {
    planCode = 'enterprise',
    planName = null,
    message = null,
    contactEmail = null,
    contactPhone = null,
    employeeCount = null,
    composedMessage = null,
    source = 'web',
    requestedByName = null,
  },
  toast
) =>
  apiCall(
    '/api/billing/subscription/request-upgrade',
    {
      method: 'POST',
      body: JSON.stringify({
        planCode,
        planName,
        message,
        contactEmail,
        contactPhone,
        employeeCount,
        composedMessage,
        source,
        requestedByName,
      }),
    },
    toast
  );

// ── Invoices ────────────────────────────────────────────────────────────────
export const listInvoices = ({ page = 1, limit = 25, status } = {}, toast) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status) params.set('status', status);
  return apiCall(`/api/billing/invoices?${params.toString()}`, { method: 'GET' }, toast);
};

export const getInvoice = (id, toast) =>
  apiCall(`/api/billing/invoices/${encodeURIComponent(id)}`, { method: 'GET' }, toast);

// Returns a URL the browser can open in a new tab to download the PDF.
// The auth middleware accepts the JWT via ?token=<jwt> for direct downloads.
export const invoicePdfUrl = (id) => {
  const base = process.env.REACT_APP_API_BASE_URL || '';
  const token = localStorage.getItem('accessToken') || '';
  return `${base}/api/billing/invoices/${encodeURIComponent(id)}/pdf?token=${encodeURIComponent(token)}`;
};

// Switches billing cycle for an issued invoice before payment.
// Voids the old invoice and generates a new one with the selected cycle.
export const updateInvoiceCycle = (invoiceId, billingCycle, toast) =>
  apiCall(
    `/api/billing/invoices/${encodeURIComponent(invoiceId)}/update-cycle`,
    { method: 'POST', body: JSON.stringify({ billingCycle }) },
    toast
  );

// ── Transactions ────────────────────────────────────────────────────────────
export const listTransactions = ({ page = 1, limit = 25, status } = {}, toast) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status) params.set('status', status);
  return apiCall(`/api/billing/transactions?${params.toString()}`, { method: 'GET' }, toast);
};

export const getTransaction = (id, toast) =>
  apiCall(`/api/billing/transactions/${encodeURIComponent(id)}`, { method: 'GET' }, toast);

// ── Legacy/back-compat ──────────────────────────────────────────────────────
// Existing app code calls this — the backend now returns a superset.
export const checkAccessStatus = (toast) =>
  apiCall('/api/subscription/check-status', { method: 'GET' }, toast);
